/* eslint-disable react-hooks/exhaustive-deps */
import L from 'leaflet'
import { useEffect, useState } from 'react'

import { useTheme } from '@mui/material'

import { MAP_STYLES, MapStyle } from '../models/MapStyle'
import { mapAuth, mapBaseUrl, mapLiveTrafficTileBaseUrl, mapOptQueryParams } from './consts'

export type UseLeafletMapArgs = L.MapOptions & {
  node: HTMLElement | null
  mapStyleOption?: MapStyle
  showDrawControl?: boolean
}

export const useLeafletMap = ({
  node,
  mapStyleOption,
  center,
  zoom,
}: UseLeafletMapArgs) => {
  const [map, setMap] = useState<L.Map | null>(null)
  const theme = useTheme()
  const mapTheme = theme.palette.mode === 'dark' ? 'night' : 'day'

  // Initialize the map
  useEffect(() => {
    if (node === null) {
      return
    }

    // Initialize the map
    let leafletMap = map

    if (leafletMap === null) {
      leafletMap = L.map(node, {
        attributionControl: false,
        center: center,
        zoom: zoom,
        zoomControl: true,
        preferCanvas: true,
        minZoom: 2,
        maxBounds: [
          [-90, -180],
          [90, 180],
        ],
      })
    }

    // Add Map Layer
    const baseLayerUrl = `${mapBaseUrl}{z}/{x}/{y}/png8${mapAuth}${mapOptQueryParams}`
    const normalMapStyle = MAP_STYLES[mapTheme][mapStyleOption || 'explore']
    const tileLayer = L.tileLayer(`${baseLayerUrl}${normalMapStyle}`, { noWrap: true }).addTo(leafletMap)

    // Add Layer Control
    const normalTrafficStyle = MAP_STYLES[mapTheme].explore_logistics
    const normalTrafficLayer = L.tileLayer(`${baseLayerUrl}${normalTrafficStyle}`, { noWrap: true })

    const normalTruckStyle = MAP_STYLES[mapTheme].explore_truck
    const normalTruckLayer = L.tileLayer(`${baseLayerUrl}${normalTruckStyle}`, { noWrap: true })

    const satelliteStyle = MAP_STYLES[mapTheme].satellite
    const satelliteLayer = L.tileLayer(`${baseLayerUrl}${satelliteStyle}`, { noWrap: true })

    const satelliteTrafficStyle = MAP_STYLES[mapTheme].satellite_logistics
    const satelliteTrafficLayer = L.tileLayer(`${baseLayerUrl}${satelliteTrafficStyle}`, { noWrap: true })

    const satelliteTruckStyle = MAP_STYLES[mapTheme].satellite_truck
    const satelliteTruckLayer = L.tileLayer(`${baseLayerUrl}${satelliteTruckStyle}`, { noWrap: true })

    // Live Traffic Flow Layer Add on
    const liveTrafficFlowUrl = `${mapLiveTrafficTileBaseUrl}{z}/{x}/{y}/png8${mapAuth}${mapOptQueryParams}`
    const liveTrafficFlowLayer = L.tileLayer(liveTrafficFlowUrl)

    const baseMaps = {
      'Normal': tileLayer,
      'Normal - Logistics': normalTrafficLayer,
      'Normal - Truck': normalTruckLayer,
      'Satellite': satelliteLayer,
      'Satellite - Logistics': satelliteTrafficLayer,
      'Satellite - Truck': satelliteTruckLayer,
    }

    // Add overlays - layers that can be toggled on/off independently of base layers
    const overlayMaps = {
      'Live Traffic Flow': liveTrafficFlowLayer,
    }
    const layerControls = L.control.layers(baseMaps, overlayMaps, { position: 'topleft' }).addTo(leafletMap)

    // Add Scale
    const layerScale = L.control.scale({
      imperial: false,
      updateWhenIdle: true,
      position: 'bottomright',
    }).addTo(leafletMap)

    // Set Map
    setMap(leafletMap)

    return () => {
      // Cleanup on component unmount
      tileLayer.remove()
      layerControls.remove()
      layerScale.remove()

      if (leafletMap) {
        leafletMap.eachLayer((layer) => {
          leafletMap!.removeLayer(layer)
        })
      }
    }
  }, [node, mapTheme])

  return { map }
}